import React from 'react';
import { Lead, Task, Opportunity } from '../../types';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface PerformanceTrendsProps {
  leads: Lead[];
  tasks: Task[];
  opportunities: Opportunity[];
}

const PerformanceTrends: React.FC<PerformanceTrendsProps> = ({ leads, tasks, opportunities }) => {
  // Calculate 7-day trends
  const getLast7DaysTrend = () => {
    const days: number[] = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const count = leads.filter(l => {
        const createdAt = new Date(l.createdAt);
        return createdAt >= date && createdAt < nextDate;
      }).length;
      
      days.push(count);
    }
    
    return days;
  };

  const getTaskCompletionRate = () => {
    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentTasks = tasks.filter(t => new Date(t.createdAt || t.dueDate) >= last7Days);
    const completed = recentTasks.filter(t => t.completed).length;
    return recentTasks.length > 0 ? (completed / recentTasks.length) * 100 : 0;
  };

  const getConversionTrend = () => {
    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentOpps = opportunities.filter(o => {
      const d = o.updatedAt || o.createdAt;
      return d && new Date(d) >= last7Days;
    });
    const won = recentOpps.filter(o => o.stage === 'won').length;
    return recentOpps.length > 0 ? (won / recentOpps.length) * 100 : 0;
  };

  const leadsTrend = getLast7DaysTrend();
  const taskCompletionRate = getTaskCompletionRate();
  const conversionRate = getConversionTrend();

  const getTrendDirection = (data: number[]) => {
    if (data.length < 2) return 'stable';
    const recent = data.slice(-3).reduce((a, b) => a + b, 0) / 3;
    const older = data.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
    if (recent > older * 1.1) return 'up';
    if (recent < older * 0.9) return 'down';
    return 'stable';
  };

  const Sparkline: React.FC<{ data: number[] }> = ({ data }) => {
    if (data.every(v => v === 0)) {
      return <div className="h-8 flex items-center text-xs text-txt-muted">No data</div>;
    }

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    return (
      <div className="flex items-end space-x-1 h-8">
        {data.map((value, i) => {
          const heightPercent = ((value - min) / range) * 100;
          return (
            <div
              key={i}
              className="flex-1 bg-info rounded-sm transition-all hover:bg-accent"
              style={{ height: `${Math.max(heightPercent, 10)}%` }}
              title={`Day ${i + 1}: ${value}`}
            />
          );
        })}
      </div>
    );
  };

  const trendDirection = getTrendDirection(leadsTrend);

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center space-x-2 mb-4">
        <div className="p-2 bg-accent-soft rounded-lg">
          <TrendingUp className="w-5 h-5 text-accent" />
        </div>
        <h3 className="text-lg font-semibold text-txt">7-Day Performance</h3>
      </div>

      <div className="space-y-4">
        {/* Leads Trend */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-txt-secondary">New Leads</span>
            <div className="flex items-center space-x-1">
              {trendDirection === 'up' && <TrendingUp className="w-4 h-4 text-success" />}
              {trendDirection === 'down' && <TrendingDown className="w-4 h-4 text-danger" />}
              {trendDirection === 'stable' && <Minus className="w-4 h-4 text-txt-secondary" />}
              <span className={`text-xs font-semibold ${
                trendDirection === 'up' ? 'text-success' : 
                trendDirection === 'down' ? 'text-danger' : 'text-txt-secondary'
              }`}>
                {leadsTrend[leadsTrend.length - 1]} today
              </span>
            </div>
          </div>
          <Sparkline data={leadsTrend} />
        </div>

        {/* Task Completion */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-txt-secondary">Task Completion</span>
            <span className="text-xs font-semibold text-txt">
              {taskCompletionRate.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-bg-surface-hover rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all"
              style={{ width: `${taskCompletionRate}%` }}
            />
          </div>
        </div>

        {/* Conversion Rate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-txt-secondary">Conversion (7d)</span>
            <span className="text-xs font-semibold text-txt">
              {conversionRate.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-bg-surface-hover rounded-full h-2">
            <div 
              className="bg-info h-2 rounded-full transition-all"
              style={{ width: `${conversionRate}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrends;

